// src/components/dashboards/BillingManager.jsx
// Role: BILLING_MANAGER — price control + billing oversight + discount tracking
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import { labAPI } from '../../services/api';
import Calendar from '../shared/Calendar';
import { notify, confirmDialog } from '../../utils/notify';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  white:      '#ffffff',
  bg:         '#f7f8fc',
  bgAlt:      '#eef0f7',
  border:     '#e4e7ef',
  borderMid:  '#c8cedd',
  ink:        '#111827',
  inkMid:     '#1f2937',
  inkSoft:    '#6b7280',
  inkGhost:   '#9ca3af',
  primary:    '#2563eb',
  primaryBg:  '#eff6ff',
  primaryBdr: '#bfdbfe',
  primaryDk:  '#1d4ed8',
  green:      '#059669',
  greenBg:    '#ecfdf5',
  greenBdr:   '#a7f3d0',
  greenDk:    '#047857',
  red:        '#dc2626',
  redBg:      '#fef2f2',
  redBdr:     '#fecaca',
  orange:     '#d97706',
  orangeBg:   '#fffbeb',
  orangeBdr:  '#fde68a',
  violet:     '#7c3aed',
  violetBg:   '#f5f3ff',
  violetBdr:  '#ddd6fe',
  teal:       '#0891b2',
  tealBg:     '#ecfeff',
  tealBdr:    '#a5f3fc',
  amber:      '#b45309',
  amberBg:    '#fffbeb',
  amberBdr:   '#fde68a',
  indigo:     '#4338ca',
  indigoBg:   '#eef2ff',
  indigoBdr:  '#c7d2fe',
  mono:       "'Roboto Mono', 'Courier New', monospace",
  font:       "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const STATUS_MAP = {
  ORDERED:          { label: 'Ordered',          color: C.inkGhost, bg: C.bg        },
  BILLED:           { label: 'Billed',           color: C.primary,  bg: C.primaryBg },
  PAID:             { label: 'Paid',             color: C.green,    bg: C.greenBg   },
  SAMPLE_COLLECTED: { label: 'Sample Collected', color: C.orange,   bg: C.orangeBg  },
  IN_PROGRESS:      { label: 'In Progress',      color: C.teal,     bg: C.tealBg    },
  COMPLETED:        { label: 'Completed',        color: C.indigo,   bg: C.indigoBg  },
  CANCELLED:        { label: 'Cancelled',        color: C.red,      bg: C.redBg     },
};

const PAY_COLORS = {
  Cash:         { color: C.green,   bg: C.greenBg   },
  UPI:          { color: C.primary, bg: C.primaryBg },
  Card:         { color: C.violet,  bg: C.violetBg  },
  'Net Banking':{ color: C.teal,    bg: C.tealBg    },
  Insurance:    { color: C.indigo,  bg: C.indigoBg  },
  Other:        { color: C.inkSoft, bg: C.bg         },
};

const CAT = {
  Microbiology: { color: C.amber,  bg: C.amberBg,  bdr: C.amberBdr,  icon: '🦠' },
  Pathology:    { color: C.violet, bg: C.violetBg, bdr: C.violetBdr, icon: '🔬' },
  Biochemistry: { color: C.teal,   bg: C.tealBg,   bdr: C.tealBdr,   icon: '⚗️' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt     = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const pct     = (part, total) => total ? ((part / total) * 100).toFixed(1) : '0.0';

// Date comparisons use the viewer's own browser timezone
const today   = () => new Date().toLocaleDateString('en-CA');               // YYYY-MM-DD, browser-local
const toLocalDate = (isoStr) => isoStr
  ? new Date(isoStr).toLocaleDateString('en-CA')                            // YYYY-MM-DD, browser-local
  : '';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const isSameDate = (isoStr, dateStr) => {
  if (!isoStr || !dateStr) return false;
  return toLocalDate(isoStr) === dateStr;                                    // both browser-local
};

// ─── Font + minimal animation injection ──────────────────────────────────────
const injectFont = () => {
  if (document.getElementById('bm3-font')) return;
  const s = document.createElement('style');
  s.id = 'bm3-font';
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Roboto+Mono:wght@400;500;700&display=swap');
    .bm3-row:hover td { background: #f7f8fc !important; }
    .bm3-overlay {
      position:fixed; inset:0; background:rgba(17,24,39,.5);
      display:flex; align-items:center; justify-content:center;
      z-index:9999; backdrop-filter:blur(4px); animation:bm3f .15s ease;
    }
    .bm3-modal {
      background:#fff; border-radius:18px;
      box-shadow:0 24px 80px rgba(17,24,39,.18), 0 0 0 1px #e4e7ef;
      animation:bm3r .2s ease;
    }
    @keyframes bm3f { from{opacity:0} to{opacity:1} }
    @keyframes bm3r { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
    .bm3-input-el:focus { border-color:#2563eb !important; box-shadow:0 0 0 3px #eff6ff !important; }
    .bm3-input-el::placeholder { color:#9ca3af; }
    .bm3-select-el:focus { border-color:#2563eb !important; outline:none; }
    .bm3-price-el:focus { border-color:#059669 !important; box-shadow:0 0 0 2px #ecfdf5 !important; }
    @media print { .bm3-no-print { display:none !important; } }
  `;
  document.head.appendChild(s);
};

// ─── Style builders ───────────────────────────────────────────────────────────
const inputStyle = (extra = {}) => ({
  width: '100%', padding: '10px 14px',
  border: `1.5px solid ${C.border}`, borderRadius: 10,
  fontSize: 14, fontFamily: C.font,
  color: C.ink, background: C.white,
  outline: 'none', boxSizing: 'border-box',
  ...extra,
});

const selectStyle = (extra = {}) => ({
  padding: '10px 14px',
  border: `1.5px solid ${C.border}`, borderRadius: 10,
  fontSize: 13, fontFamily: C.font, fontWeight: 600,
  color: C.ink, background: C.white,
  cursor: 'pointer', outline: 'none',
  appearance: 'auto', boxSizing: 'border-box',
  ...extra,
});

const btnStyle = ({ variant = 'outline', size = 'md', extra = {} } = {}) => {
  const pad = size === 'sm' ? '7px 16px' : size === 'xs' ? '5px 10px' : '10px 20px';
  const fz  = size === 'sm' ? 12 : size === 'xs' ? 11 : 13;
  const base = {
    padding: pad, border: 'none', borderRadius: 10,
    fontSize: fz, fontWeight: 700, fontFamily: C.font,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
    gap: 6, whiteSpace: 'nowrap', outline: 'none', boxSizing: 'border-box',
  };
  const variants = {
    outline:  { background: C.white, border: `1.5px solid ${C.border}`, color: C.inkSoft },
    primary:  { background: C.primary, color: '#fff', boxShadow: '0 2px 8px rgba(37,99,235,.25)' },
    green:    { background: C.green,   color: '#fff', boxShadow: '0 2px 8px rgba(5,150,105,.22)' },
    indigo:   { background: C.indigo,  color: '#fff', boxShadow: '0 2px 8px rgba(67,56,202,.22)' },
    orange:   { background: C.orange,  color: '#fff', boxShadow: '0 2px 8px rgba(217,119,6,.22)'  },
    ghost:    { background: 'transparent', border: `1.5px solid ${C.border}`, color: C.inkSoft },
  };
  return { ...base, ...variants[variant], ...extra };
};

const thS = {
  padding: '11px 18px', textAlign: 'left',
  background: C.bg, color: C.inkGhost,
  fontWeight: 700, fontSize: 11,
  textTransform: 'uppercase', letterSpacing: '.07em',
  borderBottom: `1.5px solid ${C.border}`,
  fontFamily: C.font,
};
const tdS = {
  padding: '13px 18px', borderBottom: `1px solid ${C.border}`,
  color: C.ink, verticalAlign: 'middle', fontSize: 13,
  fontFamily: C.font,
};

// ─── Small reusable components ────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const m = STATUS_MAP[status] || { label: status, color: C.inkSoft, bg: C.bg };
  return (
    <span style={{ padding: '4px 11px', borderRadius: 99, fontSize: 11, fontWeight: 700,
      background: m.bg, color: m.color, border: `1px solid ${m.color}25`, fontFamily: C.font }}>
      {m.label}
    </span>
  );
};

const PayBadge = ({ method }) => {
  const m = PAY_COLORS[method] || PAY_COLORS.Other;
  return (
    <span style={{ padding: '4px 11px', borderRadius: 99, fontSize: 11, fontWeight: 700,
      background: m.bg, color: m.color, fontFamily: C.font }}>
      {method || 'Cash'}
    </span>
  );
};

const StatCard = ({ label, value, sub, color = C.primary, icon, topColor }) => (
  <div style={{ background: C.white, borderRadius: 14, padding: '20px 22px',
    border: `1.5px solid ${C.border}`, borderTop: `3px solid ${topColor || color}`,
    boxShadow: '0 1px 4px rgba(17,24,39,.05)', fontFamily: C.font }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.inkGhost,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 10 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1, fontFamily: C.mono }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: C.inkSoft, marginTop: 6 }}>{sub}</div>}
      </div>
      <div style={{ fontSize: 26, opacity: .7 }}>{icon}</div>
    </div>
  </div>
);

const SearchBar = ({ value, onChange, placeholder, width = 300 }) => (
  <div style={{ position: 'relative', width }}>
    <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)',
      fontSize: 14, color: C.inkGhost, pointerEvents: 'none' }}>🔍</span>
    <input className="bm3-input-el" value={value} onChange={onChange} placeholder={placeholder}
      style={inputStyle({ paddingLeft: 40, height: 42 })} />
  </div>
);

const FilterBar = ({ children }) => (
  <div style={{ padding: '14px 20px', borderBottom: `1.5px solid ${C.border}`,
    display: 'flex', gap: 12, alignItems: 'center', background: C.bg, flexWrap: 'wrap' }}>
    {children}
  </div>
);

const RecordCount = ({ count, label = 'records' }) => (
  <div style={{ marginLeft: 'auto', fontSize: 13, color: C.inkSoft, fontWeight: 700, fontFamily: C.font }}>
    {count} {label}
  </div>
);

const Divider = () => (
  <div style={{ borderTop: `1px dashed ${C.border}`, margin: '0' }} />
);

// ─── Print & Download utilities ───────────────────────────────────────────────
const buildReportHTML = ({ title, subtitle, rows, columns, totalsRow, generatedBy, dateRange }) => {
  const headerCells  = columns.map(c => `<th>${c}</th>`).join('');
  const bodyRows     = rows.map(r =>
    `<tr>${r.map(cell => `<td>${cell ?? '—'}</td>`).join('')}</tr>`
  ).join('');
  const totalsHtml   = totalsRow
    ? `<tr class="totals">${totalsRow.map(c => `<td>${c ?? ''}</td>`).join('')}</tr>`
    : '';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${title}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&family=Roboto+Mono:wght@400;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Plus Jakarta Sans', sans-serif; background: #fff; color: #111827;
           padding: 32px 40px; font-size: 13px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start;
              margin-bottom: 28px; padding-bottom: 20px; border-bottom: 2px solid #e4e7ef; }
    .logo { display: flex; align-items: center; gap: 12px; }
    .logo-icon { width: 44px; height: 44px; border-radius: 10px;
                 background: linear-gradient(135deg,#2563eb,#059669);
                 display: flex; align-items: center; justify-content: center;
                 font-size: 22px; color: #fff; }
    .logo-text h1 { font-size: 18px; font-weight: 800; color: #111827; }
    .logo-text p  { font-size: 12px; color: #6b7280; margin-top: 2px; }
    .meta { text-align: right; font-size: 11px; color: #6b7280; line-height: 1.8; }
    .meta strong { color: #374151; }
    h2 { font-size: 16px; font-weight: 800; color: #111827; margin-bottom: 18px; }
    .stats { display: flex; gap: 16px; margin-bottom: 24px; flex-wrap: wrap; }
    .stat { background: #f7f8fc; border: 1.5px solid #e4e7ef; border-radius: 10px;
            padding: 14px 18px; min-width: 140px; flex: 1; }
    .stat-label { font-size: 10px; font-weight: 700; color: #9ca3af;
                  text-transform: uppercase; letter-spacing: .07em; margin-bottom: 6px; }
    .stat-value { font-family: 'Roboto Mono', monospace; font-size: 20px; font-weight: 800; color: #059669; }
    .stat-value.blue { color: #2563eb; }
    .stat-value.orange { color: #d97706; }
    .stat-value.violet { color: #7c3aed; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    thead th { background: #f7f8fc; color: #9ca3af; font-size: 10px; font-weight: 700;
               text-transform: uppercase; letter-spacing: .07em; padding: 10px 14px;
               border-bottom: 1.5px solid #e4e7ef; text-align: left; }
    tbody td { padding: 11px 14px; border-bottom: 1px solid #e4e7ef;
               font-size: 12px; color: #111827; vertical-align: middle; }
    tbody tr:nth-child(even) td { background: #fafbfe; }
    tbody tr:last-child td { border-bottom: none; }
    tr.totals td { background: #eff6ff !important; font-weight: 800; font-size: 13px;
                   border-top: 2px solid #bfdbfe; color: #1d4ed8; font-family: 'Roboto Mono', monospace; }
    .mono { font-family: 'Roboto Mono', monospace; font-weight: 700; }
    .green { color: #059669; } .red { color: #dc2626; }
    .orange { color: #d97706; } .blue { color: #2563eb; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 99px; font-size: 10px; font-weight: 700; }
    .footer { margin-top: 28px; padding-top: 16px; border-top: 1px solid #e4e7ef;
              font-size: 11px; color: #9ca3af; display: flex; justify-content: space-between; }
    @media print {
      body { padding: 20px 24px; }
      @page { margin: 1cm; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo">
      <div class="logo-icon">🏥</div>
      <div class="logo-text">
        <h1>Hospital Lab Billing</h1>
        <p>Billing Manager — Official Report</p>
      </div>
    </div>
    <div class="meta">
      <div><strong>Report:</strong> ${title}</div>
      ${subtitle ? `<div><strong>Period:</strong> ${subtitle}</div>` : ''}
      ${generatedBy ? `<div><strong>Generated by:</strong> ${generatedBy}</div>` : ''}
      <div><strong>Generated on:</strong> ${new Date().toLocaleString('en-IN')}</div>
    </div>
  </div>
  <table>
    <thead><tr>${headerCells}</tr></thead>
    <tbody>${bodyRows}${totalsHtml}</tbody>
  </table>
  <div class="footer">
    <span>🏥 Hospital Lab Billing System</span>
    <span>Confidential — For Internal Use Only</span>
  </div>
  <script>
    window.onload = () => { window.print(); }
  </script>
</body>
</html>`;
};

const printReport = (config) => {
  const html = buildReportHTML(config);
  const win  = window.open('', '_blank', 'width=900,height=700');
  win.document.write(html);
  win.document.close();
};

const downloadCSV = (filename, columns, rows) => {
  const escape = (v) => {
    const s = String(v ?? '').replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [
    columns.map(escape).join(','),
    ...rows.map(r => r.map(escape).join(',')),
  ].join('\n');
  const blob = new Blob(['\uFEFF' + lines], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// ─── Discount Modal ───────────────────────────────────────────────────────────
const DiscountModal = ({ order, onClose, onSuccess }) => {
  const [discType,   setDiscType]   = useState('flat');
  const [discValue,  setDiscValue]  = useState('');
  const [discReason, setDiscReason] = useState('');
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  const base   = parseFloat(order.bill_amount || 0);
  const flat   = discType === 'flat'
    ? Math.min(parseFloat(discValue || 0), base)
    : (base * Math.min(parseFloat(discValue || 0), 100)) / 100;
  const final  = Math.max(0, base - flat);
  const hasVal = discValue && parseFloat(discValue) > 0;

  const handleApply = async () => {
    if (!discValue || parseFloat(discValue) <= 0) { setError('Enter a valid discount value.'); return; }
    if (!discReason.trim()) { setError('Reason is required.'); return; }
    setLoading(true); setError('');
    try {
      const updated = await labAPI.applyDiscount(order.id, {
        discount_type: discType, discount_value: parseFloat(discValue),
        discount_reason: discReason, final_amount: final,
      });
      onSuccess(updated);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to apply discount');
    } finally { setLoading(false); }
  };

  const labelS = { fontSize: 11, fontWeight: 700, color: C.inkSoft, display: 'block',
    marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.07em', fontFamily: C.font };

  return (
    <div className="bm3-overlay" onClick={onClose}>
      <div className="bm3-modal" style={{ width: 480, fontFamily: C.font }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding: '22px 26px', borderBottom: `1.5px solid ${C.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.ink, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: C.orangeBg,
                border: `1.5px solid ${C.orangeBdr}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🏷️</div>
              Apply Discount
            </div>
            <div style={{ fontSize: 13, color: C.inkSoft, marginTop: 6, marginLeft: 48 }}>
              Order #{order.id} — <strong style={{ color: C.inkMid }}>{order.patient_name}</strong>
            </div>
          </div>
          <button onClick={onClose} style={btnStyle({ variant: 'outline', size: 'xs',
            extra: { width: 32, height: 32, borderRadius: 8, padding: 0, justifyContent: 'center' } })}>✕</button>
        </div>
        <div style={{ padding: '22px 26px' }}>
          <div style={{ background: C.bg, border: `1.5px solid ${C.border}`, borderRadius: 12,
            padding: '14px 18px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: C.inkSoft, fontWeight: 600 }}>Original Bill Amount</span>
            <span style={{ fontFamily: C.mono, fontWeight: 800, fontSize: 18, color: C.ink }}>{fmt(base)}</span>
          </div>
          <div style={{ display: 'grid', gap: 18 }}>
            <div>
              <label style={labelS}>Discount Type</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['flat', 'percent'].map(t => (
                  <button key={t} onClick={() => { setDiscType(t); setDiscValue(''); }}
                    style={{ flex: 1, padding: '10px 8px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                      fontFamily: C.font, cursor: 'pointer', textAlign: 'center', outline: 'none',
                      border: `1.5px solid ${discType === t ? C.primary : C.border}`,
                      background: discType === t ? C.primaryBg : C.white,
                      color: discType === t ? C.primary : C.inkSoft }}>
                    {t === 'flat' ? '₹ Flat Amount' : '% Percentage'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={labelS}>{discType === 'flat' ? 'Discount Amount (₹)' : 'Discount Percentage (%)'}</label>
              <input className="bm3-input-el" type="number" min="0"
                max={discType === 'percent' ? 100 : base}
                placeholder={discType === 'flat' ? 'e.g. 200' : 'e.g. 15'}
                value={discValue} onChange={e => setDiscValue(e.target.value)}
                style={inputStyle({ height: 42 })} />
            </div>
            <div>
              <label style={labelS}>Reason for Discount *</label>
              <textarea className="bm3-input-el" rows={2}
                placeholder="e.g. BPL card, staff discount, insurance waiver…"
                value={discReason} onChange={e => setDiscReason(e.target.value)}
                style={inputStyle({ resize: 'vertical', minHeight: 68 })} />
            </div>
          </div>
          {hasVal && (
            <div style={{ marginTop: 18, background: C.greenBg, border: `1.5px solid ${C.greenBdr}`,
              borderRadius: 12, padding: '16px 18px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.green,
                textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>Preview</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: C.inkSoft, fontSize: 13 }}>Original</span>
                <span style={{ fontFamily: C.mono, color: C.inkSoft, textDecoration: 'line-through' }}>{fmt(base)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ color: C.red, fontSize: 13, fontWeight: 600 }}>
                  Discount ({discType === 'percent' ? `${discValue}%` : 'flat'})
                </span>
                <span style={{ fontFamily: C.mono, color: C.red, fontWeight: 700 }}>− {fmt(flat)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                borderTop: `1.5px solid ${C.greenBdr}`, paddingTop: 10 }}>
                <span style={{ color: C.greenDk, fontWeight: 800, fontSize: 15 }}>Final Amount</span>
                <span style={{ fontFamily: C.mono, fontWeight: 800, fontSize: 18, color: C.green }}>{fmt(final)}</span>
              </div>
            </div>
          )}
          {error && (
            <div style={{ marginTop: 16, background: C.redBg, border: `1.5px solid ${C.redBdr}`,
              color: C.red, padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600 }}>
              ⚠ {error}
            </div>
          )}
        </div>
        <div style={{ padding: '18px 26px', borderTop: `1.5px solid ${C.border}`,
          display: 'flex', gap: 10, justifyContent: 'flex-end', background: C.bg, borderRadius: '0 0 18px 18px' }}>
          <button style={btnStyle({ variant: 'outline' })} onClick={onClose}>Cancel</button>
          <button style={btnStyle({ variant: 'green', extra: { opacity: loading ? .6 : 1 } })}
            onClick={handleApply} disabled={loading}>
            {loading ? '⏳ Applying…' : '✓ Apply Discount'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Price Edit Modal ─────────────────────────────────────────────────────────
const PriceEditModal = ({ test, onClose, onSuccess }) => {
  const [newPrice, setNewPrice] = useState(String(test.price || ''));
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const old  = parseFloat(test.price || 0);
  const np   = parseFloat(newPrice || 0);
  const diff = np - old;
  const catS = CAT[test.category_name] || { color: C.primary, bg: C.primaryBg, bdr: C.primaryBdr, icon: '🧪' };

  const handleSave = async () => {
    if (!newPrice || np <= 0) { setError('Price must be greater than 0.'); return; }
    setLoading(true); setError('');
    try {
      const updated = await labAPI.updateTestPrice(test.id, { price: np });
      onSuccess(updated);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update price');
    } finally { setLoading(false); }
  };

  return (
    <div className="bm3-overlay" onClick={onClose}>
      <div className="bm3-modal" style={{ width: 420, fontFamily: C.font }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '22px 26px', borderBottom: `1.5px solid ${C.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ padding: '4px 12px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                background: catS.bg, color: catS.color, border: `1px solid ${catS.bdr}` }}>
                {catS.icon} {test.category_name}
              </span>
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>{test.name}</div>
            <div style={{ fontSize: 12, color: C.inkGhost, marginTop: 3, fontFamily: C.mono }}>{test.code}</div>
          </div>
          <button onClick={onClose} style={btnStyle({ variant: 'outline', size: 'xs',
            extra: { width: 32, height: 32, borderRadius: 8, padding: 0, justifyContent: 'center' } })}>✕</button>
        </div>
        <div style={{ padding: '22px 26px' }}>
          <div style={{ background: C.bg, border: `1.5px solid ${C.border}`, borderRadius: 12,
            padding: '14px 18px', marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: C.inkSoft, marginBottom: 4 }}>Current Price</div>
            <div style={{ fontFamily: C.mono, fontWeight: 800, fontSize: 24, color: C.inkMid }}>{fmt(old)}</div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: C.inkSoft, display: 'block',
              marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.07em' }}>New Price (₹)</label>
            <input className="bm3-input-el" type="number" min="0" step="0.01"
              placeholder="Enter new price" value={newPrice} onChange={e => setNewPrice(e.target.value)}
              style={inputStyle({ fontFamily: C.mono, fontWeight: 700, fontSize: 16, height: 46,
                color: diff > 0 ? C.red : diff < 0 ? C.green : C.ink })} />
          </div>
          {newPrice && np > 0 && np !== old && (
            <div style={{ marginTop: 14, padding: '12px 16px', borderRadius: 10,
              background: diff > 0 ? C.redBg : C.greenBg,
              border: `1.5px solid ${diff > 0 ? C.redBdr : C.greenBdr}`,
              display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 20 }}>{diff > 0 ? '📈' : '📉'}</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 15, color: diff > 0 ? C.red : C.green }}>
                  {diff > 0 ? '+' : ''}{fmt(diff)}
                </div>
                <div style={{ fontSize: 12, color: C.inkSoft }}>
                  {diff > 0 ? 'Price increase' : 'Price decrease'} of {Math.abs((diff / old) * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          )}
          {error && (
            <div style={{ marginTop: 14, background: C.redBg, border: `1.5px solid ${C.redBdr}`,
              color: C.red, padding: '10px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600 }}>
              ⚠ {error}
            </div>
          )}
        </div>
        <div style={{ padding: '18px 26px', borderTop: `1.5px solid ${C.border}`,
          display: 'flex', gap: 10, justifyContent: 'flex-end', background: C.bg, borderRadius: '0 0 18px 18px' }}>
          <button style={btnStyle({ variant: 'outline' })} onClick={onClose}>Cancel</button>
          <button style={btnStyle({ variant: 'primary',
            extra: { opacity: (loading || np === old || np <= 0) ? .5 : 1 } })}
            onClick={handleSave} disabled={loading || np === old || np <= 0}>
            {loading ? '⏳ Saving…' : '💾 Save Price'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Billing Table (reused across multiple tabs) ──────────────────────────────
const BillingTable = ({ rows, onDiscount, showDiscountBtn = false }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>{['#', 'Patient', 'Token', 'Tests', 'Bill', 'Discount', 'Final', 'Method', 'Status', 'Date', showDiscountBtn ? 'Action' : ''].filter(Boolean).map(h => (
          <th key={h} style={thS}>{h}</th>
        ))}</tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={11} style={{ ...tdS, textAlign: 'center', padding: '48px 0', color: C.inkGhost }}>
            No records found
          </td></tr>
        ) : rows.map(o => (
          <tr key={o.id} className="bm3-row">
            <td style={{ ...tdS, fontFamily: C.mono, fontSize: 11, color: C.inkGhost }}>{o.id}</td>
            <td style={tdS}>
              <div style={{ fontWeight: 700 }}>{o.patient_name}</div>
              <div style={{ fontSize: 11, color: C.inkGhost, fontFamily: C.mono, marginTop: 2 }}>{o.patient_id}</div>
            </td>
            <td style={tdS}>
              <span style={{ fontFamily: C.mono, fontWeight: 700, color: C.primary }}>{o.token_number || '—'}</span>
            </td>
            <td style={{ ...tdS, maxWidth: 180 }}>
              <div style={{ fontSize: 11, color: C.inkSoft, lineHeight: 1.5 }}>
                {o.items?.map(i => i.test_name).join(', ')}
              </div>
            </td>
            <td style={tdS}><span style={{ fontFamily: C.mono }}>{fmt(o.bill_amount)}</span></td>
            <td style={tdS}>
              {parseFloat(o.discount_amount || 0) > 0 ? (
                <div>
                  <div style={{ color: C.orange, fontFamily: C.mono, fontSize: 12, fontWeight: 700 }}>
                    − {fmt(o.discount_amount)}
                  </div>
                  {o.discount_reason && (
                    <div style={{ fontSize: 10, color: C.inkGhost, marginTop: 2, fontStyle: 'italic' }}>
                      {o.discount_reason}
                    </div>
                  )}
                </div>
              ) : <span style={{ color: C.inkGhost }}>—</span>}
            </td>
            <td style={tdS}>
              <span style={{ fontFamily: C.mono, fontWeight: 800, color: C.green }}>
                {fmt(o.final_amount || o.bill_amount)}
              </span>
            </td>
            <td style={tdS}>
              {o.payment_method ? <PayBadge method={o.payment_method} /> : <span style={{ color: C.inkGhost }}>—</span>}
            </td>
            <td style={tdS}><StatusBadge status={o.status} /></td>
            <td style={tdS}>
              <span style={{ fontSize: 11, color: C.inkGhost }}>
                {o.order_date ? new Date(o.order_date).toLocaleDateString('en-IN') : '—'}
              </span>
            </td>
            {showDiscountBtn && (
              <td style={tdS}>
                {o.status === 'BILLED' && (
                  <button style={btnStyle({ variant: 'outline', size: 'sm' })}
                    onClick={() => onDiscount?.(o)}>🏷️ Discount</button>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ─── Report Summary Pill ──────────────────────────────────────────────────────
const SummaryPill = ({ label, value, color = C.primary }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
    background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 12,
    padding: '14px 20px', minWidth: 120 }}>
    <div style={{ fontFamily: C.mono, fontWeight: 800, fontSize: 20, color }}>{value}</div>
    <div style={{ fontSize: 11, color: C.inkGhost, fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '.07em', marginTop: 4 }}>{label}</div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const BillingManager = () => {
  const { user } = useAuth();

  // ── State ──────────────────────────────────────────────────────────────────
  const [activeTab,      setActiveTab]      = useState('overview');
  const [orders,         setOrders]         = useState([]);
  const [tests,          setTests]          = useState([]);
  const [billingMembers, setBillingMembers] = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [searchQ,        setSearchQ]        = useState('');
  const [catFilter,      setCatFilter]      = useState('all');
  const [statusFilter,   setStatusFilter]   = useState('all');
  const [priceModal,     setPriceModal]     = useState(null);
  const [discountModal,  setDiscountModal]  = useState(null);
  const [editPrices,     setEditPrices]     = useState({});

  // Member report state
  const [selectedMember,  setSelectedMember]  = useState('all');
  const [memberSearchQ,   setMemberSearchQ]   = useState('');

  // Daily report state
  const [dateMode,       setDateMode]       = useState('single');  // 'single' | 'range'
  const [reportDate,     setReportDate]     = useState(today());
  const [fromDate,       setFromDate]       = useState(today());
  const [toDate,         setToDate]         = useState(today());
  const [dailySearchQ,   setDailySearchQ]   = useState('');

  useEffect(() => { injectFont(); }, []);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await labAPI.getOrders({});
      setOrders(Array.isArray(data) ? data : data.results || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchTests = useCallback(async () => {
    try {
      const data = await labAPI.getTests();
      setTests(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  }, []);

  // Fetch billing members: try API, fall back to extracting from orders
  const fetchBillingMembers = useCallback(async () => {
    try {
      // Attempt dedicated endpoint: GET /auth/users/?role=BILLING_MEMBER
      const data = await labAPI.getBillingMembers?.();
      if (data && data.length) {
        setBillingMembers(Array.isArray(data) ? data : data.results || []);
        return;
      }
    } catch (_) { /* fall through */ }
    // Fallback: extract unique billers from order data
    // Orders are expected to have billed_by_id + billed_by_name fields
  }, []);

  useEffect(() => { fetchOrders(); fetchTests(); fetchBillingMembers(); }, [fetchOrders, fetchTests, fetchBillingMembers]);

  // Derive billing members from orders if API didn't provide them.
  // Only pulls entries where billed_by_id is a real non-null value.
  const derivedMembers = React.useMemo(() => {
    if (billingMembers.length > 0) return billingMembers;
    const map = {};
    orders.forEach(o => {
      // billed_by_id comes from LabTestOrderListSerializer (backend field)
      // Fall back to billed_by only if it's a number (not null / undefined)
      const rawId = o.billed_by_id ?? o.billed_by ?? null;
      if (!rawId) return;                          // skip unassigned orders
      const id   = String(rawId);
      const name = o.billed_by_name || `User #${id}`;
      if (!map[id]) map[id] = { id, name };
    });
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
  }, [billingMembers, orders]);

  // ── Computed stats ─────────────────────────────────────────────────────────
  const paidOrders       = orders.filter(o => ['PAID','SAMPLE_COLLECTED','IN_PROGRESS','COMPLETED'].includes(o.status));
  const totalRevenue     = paidOrders.reduce((s, o) => s + parseFloat(o.final_amount || o.bill_amount || 0), 0);
  const discountedOrders = orders.filter(o => parseFloat(o.discount_amount || 0) > 0);
  const totalDiscount    = discountedOrders.reduce((s, o) => s + parseFloat(o.discount_amount || 0), 0);
  const payBreakdown     = {};
  paidOrders.forEach(o => {
    const m = o.payment_method || 'Cash';
    if (!payBreakdown[m]) payBreakdown[m] = { count: 0, amount: 0 };
    payBreakdown[m].count++;
    payBreakdown[m].amount += parseFloat(o.final_amount || o.bill_amount || 0);
  });

  // ── Filtered sets ──────────────────────────────────────────────────────────
  const filteredOrders = orders.filter(o => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    return o.patient_name?.toLowerCase().includes(q) ||
           o.patient_id?.toLowerCase().includes(q)   ||
           o.token_number?.toLowerCase().includes(q);
  });

  const categories    = [...new Set(tests.map(t => t.category_name).filter(Boolean))];
  const filteredTests = tests.filter(t => {
    if (catFilter !== 'all' && t.category_name !== catFilter) return false;
    if (!searchQ) return true;
    return t.name.toLowerCase().includes(searchQ.toLowerCase()) ||
           t.code.toLowerCase().includes(searchQ.toLowerCase());
  });

  // Member orders — filtered by billed_by_id (set by backend on bill generation)
  const memberOrders = React.useMemo(() => {
    let res = orders;
    if (selectedMember !== 'all') {
      res = res.filter(o => {
        // billed_by_id is the primary field; billed_by is numeric FK fallback
        const rawId = o.billed_by_id ?? o.billed_by ?? null;
        if (!rawId) return false;
        return String(rawId) === String(selectedMember);
      });
    }
    if (memberSearchQ) {
      const q = memberSearchQ.toLowerCase();
      res = res.filter(o =>
        o.patient_name?.toLowerCase().includes(q) ||
        o.patient_id?.toLowerCase().includes(q)   ||
        o.token_number?.toLowerCase().includes(q)
      );
    }
    return res;
  }, [orders, selectedMember, memberSearchQ]);

  const memberRevenue  = memberOrders
    .filter(o => ['PAID','SAMPLE_COLLECTED','IN_PROGRESS','COMPLETED'].includes(o.status))
    .reduce((s, o) => s + parseFloat(o.final_amount || o.bill_amount || 0), 0);
  const memberDiscount = memberOrders.reduce((s, o) => s + parseFloat(o.discount_amount || 0), 0);

  // Per-member revenue summary (leaderboard).
  // Only counts orders that have an actual billed_by value — skips unassigned.
  const memberSummary = React.useMemo(() => {
    const map = {};
    orders.forEach(o => {
      const rawId = o.billed_by_id ?? o.billed_by ?? null;
      if (!rawId) return;                          // skip orders not assigned to a member
      const id   = String(rawId);
      const name = o.billed_by_name || `User #${id}`;
      if (!map[id]) map[id] = { id, name, orders: 0, revenue: 0, discounts: 0 };
      map[id].orders++;
      if (['PAID','SAMPLE_COLLECTED','IN_PROGRESS','COMPLETED'].includes(o.status)) {
        map[id].revenue   += parseFloat(o.final_amount || o.bill_amount || 0);
      }
      map[id].discounts += parseFloat(o.discount_amount || 0);
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [orders]);

  // Daily orders — supports both single-date and date-range modes
  const dailyOrders = React.useMemo(() => {
    let res = orders.filter(o => {
      // Always convert to a browser-local date string (YYYY-MM-DD) for consistent comparison
      const d = toLocalDate(o.order_date || o.created_at);
      if (!d) return false;
      if (dateMode === 'range') {
        if (fromDate && d < fromDate) return false;
        if (toDate   && d > toDate)   return false;
        return true;
      }
      // single mode — isSameDate also uses browser-local internally
      return isSameDate(o.order_date || o.created_at, reportDate);
    });
    if (dailySearchQ) {
      const q = dailySearchQ.toLowerCase();
      res = res.filter(o =>
        o.patient_name?.toLowerCase().includes(q) ||
        o.patient_id?.toLowerCase().includes(q)   ||
        o.token_number?.toLowerCase().includes(q)
      );
    }
    return res;
  }, [orders, dateMode, reportDate, fromDate, toDate, dailySearchQ]);

  const dailyRevenue   = dailyOrders
    .filter(o => ['PAID','SAMPLE_COLLECTED','IN_PROGRESS','COMPLETED'].includes(o.status))
    .reduce((s, o) => s + parseFloat(o.final_amount || o.bill_amount || 0), 0);
  const dailyBilled    = dailyOrders.filter(o => o.status !== 'ORDERED' && o.status !== 'CANCELLED').length;
  const dailyDiscount  = dailyOrders.reduce((s, o) => s + parseFloat(o.discount_amount || 0), 0);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handlePriceUpdated    = (u) => { setPriceModal(null);    setTests(p => p.map(t => t.id === u.id ? u : t)); };
  const handleDiscountApplied = (u) => { setDiscountModal(null); setOrders(p => p.map(o => o.id === u.id ? u : o)); };

  const inlinePriceSave = async (testId, val) => {
    if (!val || parseFloat(val) <= 0) return;
    try {
      const u = await labAPI.updateTestPrice(testId, { price: parseFloat(val) });
      setTests(p => p.map(t => t.id === u.id ? u : t));
      setEditPrices(p => { const n = { ...p }; delete n[testId]; return n; });
    } catch { notify.error('Failed to update price'); }
  };

  // ── Print handlers ─────────────────────────────────────────────────────────
  const orderColumns = ['#', 'Patient', 'Patient ID', 'Token', 'Tests', 'Bill Amount', 'Discount', 'Final Amount', 'Payment Method', 'Status', 'Date'];

  const ordersToRows = (list) => list.map(o => [
    o.id,
    o.patient_name,
    o.patient_id,
    o.token_number || '—',
    o.items?.map(i => i.test_name).join(', '),
    fmt(o.bill_amount),
    parseFloat(o.discount_amount || 0) > 0 ? `− ${fmt(o.discount_amount)}` : '—',
    fmt(o.final_amount || o.bill_amount),
    o.payment_method || 'Cash',
    (STATUS_MAP[o.status] || { label: o.status }).label,
    o.order_date ? fmtDate(o.order_date) : '—',
  ]);

  const dailyDateLabel = dateMode === 'range'
    ? (fromDate && toDate
        ? fromDate === toDate
          ? fmtDate(fromDate)
          : `${fmtDate(fromDate)} – ${fmtDate(toDate)}`
        : fromDate ? `From ${fmtDate(fromDate)}` : toDate ? `Until ${fmtDate(toDate)}` : 'All Dates')
    : fmtDate(reportDate);

  const handlePrintDailyReport = () => {
    const rows = ordersToRows(dailyOrders);
    const totals = [
      '', `Total: ${dailyOrders.length} orders`, '', '', '',
      fmt(dailyOrders.reduce((s,o) => s + parseFloat(o.bill_amount||0), 0)),
      dailyDiscount > 0 ? `− ${fmt(dailyDiscount)}` : '—',
      fmt(dailyRevenue), '', '', '',
    ];
    printReport({
      title: `Billing Report — ${dailyDateLabel}`,
      subtitle: dailyDateLabel,
      rows, columns: orderColumns, totalsRow: totals,
      generatedBy: user?.first_name || user?.username,
    });
  };

  const handleDownloadDailyCSV = () => {
    const rows = ordersToRows(dailyOrders);
    const safeName = dateMode === 'range'
      ? `${fromDate || 'start'}-to-${toDate || 'end'}`
      : reportDate;
    downloadCSV(`billing-report-${safeName}.csv`, orderColumns, rows);
  };

  const selectedMemberName = selectedMember === 'all'
    ? 'All Members'
    : (derivedMembers.find(m => String(m.id) === String(selectedMember))?.name || 'Selected Member');

  const handlePrintMemberReport = () => {
    const rows = ordersToRows(memberOrders);
    const rev  = memberOrders.filter(o => ['PAID','SAMPLE_COLLECTED','IN_PROGRESS','COMPLETED'].includes(o.status))
                             .reduce((s, o) => s + parseFloat(o.final_amount || o.bill_amount || 0), 0);
    const totals = [
      '', `Total: ${memberOrders.length} orders`, '', '', '',
      fmt(memberOrders.reduce((s,o)=>s+parseFloat(o.bill_amount||0),0)),
      memberDiscount > 0 ? `− ${fmt(memberDiscount)}` : '—',
      fmt(rev), '', '', '',
    ];
    printReport({
      title: `Member Billing Report — ${selectedMemberName}`,
      subtitle: `Billing Member: ${selectedMemberName}`,
      rows, columns: orderColumns, totalsRow: totals,
      generatedBy: user?.first_name || user?.username,
    });
  };

  const handleDownloadMemberCSV = () => {
    const rows = ordersToRows(memberOrders);
    const safeName = selectedMemberName.replace(/\s+/g, '-').toLowerCase();
    downloadCSV(`member-report-${safeName}.csv`, orderColumns, rows);
  };

  // ── Tabs definition ────────────────────────────────────────────────────────
  const TABS = [
    { key: 'overview',  emoji: '📊', label: 'Overview'          },
    { key: 'prices',    emoji: '💰', label: 'Price Management'  },
    { key: 'history',   emoji: '📋', label: 'Payment History'   },
    { key: 'discounts', emoji: '🏷️', label: 'Discounts'         },
    { key: 'members',   emoji: '👤', label: 'Member Reports'    },
    { key: 'daily',     emoji: '📅', label: 'Daily Reports'     },
    { key: 'calendar',  emoji: '🗓️', label: 'Calendar'          },
  ];

  // ── Action buttons for report tabs ────────────────────────────────────────
  const ReportActions = ({ onPrint, onCSV, count }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
      <span style={{ fontSize: 12, color: C.inkGhost, fontFamily: C.font, marginRight: 4 }}>
        {count} records
      </span>
      <button style={btnStyle({ variant: 'outline', size: 'sm' })} onClick={onCSV}>
        ⬇ Download CSV
      </button>
      <button style={btnStyle({ variant: 'indigo', size: 'sm' })} onClick={onPrint}>
        🖨 Print Report
      </button>
    </div>
  );

  return (
    <DashboardLayout title="Billing Manager">
      <div style={{ minHeight: '100vh', background: C.bg, padding: '28px 32px',
        overflowY: 'auto', fontFamily: C.font }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 50, height: 50, borderRadius: 14,
              background: 'linear-gradient(135deg, #2563eb, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 24, boxShadow: '0 4px 16px rgba(37,99,235,.28)', flexShrink: 0 }}>💼</div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.ink, lineHeight: 1.2 }}>
                Billing Manager Portal
              </h1>
              <p style={{ margin: '5px 0 0', fontSize: 13, color: C.inkSoft }}>
                Financial control &amp; oversight &nbsp;·&nbsp;
                <strong style={{ color: C.primary }}>{user?.first_name || user?.username}</strong>
                &nbsp;·&nbsp; {new Date().toLocaleDateString('en-IN', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>

        {/* ── Tab Bar ── */}
        <div style={{ display: 'flex', gap: 3, marginBottom: 24,
          background: C.bgAlt, borderRadius: 14, padding: 5,
          border: `1.5px solid ${C.border}` }}>
          {TABS.map(t => {
            const active = activeTab === t.key;
            return (
              <button key={t.key}
                onClick={() => { setActiveTab(t.key); setSearchQ(''); setCatFilter('all'); setStatusFilter('all'); }}
                style={{
                  flex: 1, padding: '9px 6px',
                  fontSize: 12, fontWeight: 700, fontFamily: C.font,
                  border: active ? `1.5px solid ${C.primaryBdr}` : '1.5px solid transparent',
                  borderRadius: 10, cursor: 'pointer',
                  background: active ? C.white : 'transparent',
                  color: active ? C.primary : C.inkSoft,
                  boxShadow: active ? '0 1px 6px rgba(37,99,235,.1)' : 'none',
                  outline: 'none', whiteSpace: 'nowrap',
                }}>
                {t.emoji} {t.label}
              </button>
            );
          })}
        </div>

        {/* ══════════════════ OVERVIEW ══════════════════ */}
        {activeTab === 'overview' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 22 }}>
              <StatCard label="Total Revenue" value={fmt(totalRevenue)} color={C.green} topColor={C.green}
                icon="💰" sub={`${paidOrders.length} paid orders`} />
              <StatCard label="Unique Patients"
                value={new Set(orders.map(o => o.patient_id)).size}
                color={C.primary} topColor={C.primary} icon="👥" sub="Total patients served" />
              <StatCard label="Total Discounts" value={fmt(totalDiscount)}
                color={C.orange} topColor={C.orange} icon="🏷️"
                sub={`${discountedOrders.length} orders discounted`} />
              <StatCard label="All Orders" value={orders.length}
                color={C.violet} topColor={C.violet} icon="📋"
                sub={`${orders.filter(o => o.status === 'ORDERED').length} pending billing`} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 22 }}>
              <div style={{ background: C.white, borderRadius: 14, padding: 22,
                border: `1.5px solid ${C.border}`, boxShadow: '0 1px 4px rgba(17,24,39,.05)' }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, marginBottom: 18 }}>Order Status Breakdown</div>
                {Object.entries(STATUS_MAP).map(([st, m]) => {
                  const cnt = orders.filter(o => o.status === st).length;
                  if (!cnt) return null;
                  return (
                    <div key={st} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                        background: m.bg, color: m.color, minWidth: 116, textAlign: 'center',
                        border: `1px solid ${m.color}22`, fontFamily: C.font }}>{m.label}</span>
                      <div style={{ flex: 1, height: 7, background: C.bgAlt, borderRadius: 99, overflow: 'hidden',
                        border: `1px solid ${C.border}` }}>
                        <div style={{ height: '100%', borderRadius: 99, background: m.color,
                          width: `${pct(cnt, orders.length)}%` }} />
                      </div>
                      <span style={{ fontFamily: C.mono, fontSize: 12, color: C.inkSoft, minWidth: 68, textAlign: 'right' }}>
                        {cnt} ({pct(cnt, orders.length)}%)
                      </span>
                    </div>
                  );
                })}
              </div>
              <div style={{ background: C.white, borderRadius: 14, padding: 22,
                border: `1.5px solid ${C.border}`, boxShadow: '0 1px 4px rgba(17,24,39,.05)' }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, marginBottom: 18 }}>Payment Method Breakdown</div>
                {Object.entries(payBreakdown).length === 0 ? (
                  <div style={{ padding: '24px 0', textAlign: 'center', color: C.inkGhost, fontSize: 13 }}>No confirmed payments yet</div>
                ) : (
                  Object.entries(payBreakdown).sort((a, b) => b[1].amount - a[1].amount).map(([m, d]) => {
                    const pc = PAY_COLORS[m] || PAY_COLORS.Other;
                    return (
                      <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 13 }}>
                        <PayBadge method={m} />
                        <div style={{ flex: 1, height: 7, background: C.bgAlt, borderRadius: 99, overflow: 'hidden',
                          border: `1px solid ${C.border}` }}>
                          <div style={{ height: '100%', borderRadius: 99, background: pc.color,
                            width: `${pct(d.amount, totalRevenue)}%` }} />
                        </div>
                        <div style={{ textAlign: 'right', minWidth: 90 }}>
                          <div style={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: pc.color }}>{fmt(d.amount)}</div>
                          <div style={{ fontSize: 11, color: C.inkGhost }}>{d.count} orders</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Recent transactions */}
            <div style={{ background: C.white, borderRadius: 14, border: `1.5px solid ${C.border}`,
              boxShadow: '0 1px 4px rgba(17,24,39,.05)', overflow: 'hidden' }}>
              <div style={{ padding: '16px 22px', borderBottom: `1.5px solid ${C.border}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>Recent Transactions</span>
                <span style={{ fontSize: 12, color: C.inkGhost }}>Latest 10 orders</span>
              </div>
              <BillingTable rows={orders.slice(0, 10)} />
            </div>
          </>
        )}

        {/* ══════════════════ PRICE MANAGEMENT ══════════════════ */}
        {activeTab === 'prices' && (
          <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`,
            boxShadow: '0 1px 4px rgba(17,24,39,.05)', overflow: 'hidden' }}>
            <FilterBar>
              <SearchBar value={searchQ} onChange={e => setSearchQ(e.target.value)}
                placeholder="Search test name or code…" width={300} />
              <select className="bm3-select-el" value={catFilter} onChange={e => setCatFilter(e.target.value)}
                style={selectStyle({ minWidth: 170 })}>
                <option value="all">All Categories</option>
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <RecordCount count={filteredTests.length} label="tests" />
            </FilterBar>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>{['Category', 'Code', 'Test Name', 'Sample Type', 'Current Price', 'Quick Edit', 'Action'].map(h => (
                    <th key={h} style={thS}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {filteredTests.map(test => {
                    const cs = CAT[test.category_name] || { color: C.primary, bg: C.primaryBg, bdr: C.primaryBdr, icon: '🧪' };
                    const draft = editPrices[test.id];
                    const hasChange = draft !== undefined && parseFloat(draft) !== parseFloat(test.price);
                    return (
                      <tr key={test.id} className="bm3-row">
                        <td style={tdS}>
                          <span style={{ padding: '4px 12px', borderRadius: 99, fontSize: 11, fontWeight: 700,
                            background: cs.bg, color: cs.color, border: `1px solid ${cs.bdr}` }}>
                            {cs.icon} {test.category_name}
                          </span>
                        </td>
                        <td style={tdS}><span style={{ fontFamily: C.mono, fontSize: 12, color: C.inkSoft }}>{test.code}</span></td>
                        <td style={{ ...tdS, fontWeight: 700 }}>{test.name}</td>
                        <td style={tdS}>
                          <span style={{ fontSize: 12, color: C.inkSoft, background: C.bg,
                            padding: '3px 10px', borderRadius: 6, border: `1px solid ${C.border}` }}>
                            {test.sample_type_display || test.sample_type}
                          </span>
                        </td>
                        <td style={tdS}>
                          <span style={{ fontFamily: C.mono, fontWeight: 800, fontSize: 14, color: C.green }}>
                            {fmt(test.price)}
                          </span>
                        </td>
                        <td style={tdS}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <span style={{ color: C.inkGhost, fontSize: 13 }}>₹</span>
                            <input className="bm3-price-el"
                              value={draft !== undefined ? draft : test.price}
                              onChange={e => setEditPrices(p => ({ ...p, [test.id]: e.target.value }))}
                              onKeyDown={e => {
                                if (e.key === 'Enter')  inlinePriceSave(test.id, editPrices[test.id]);
                                if (e.key === 'Escape') setEditPrices(p => { const n = { ...p }; delete n[test.id]; return n; });
                              }}
                              style={{ width: 92, padding: '7px 9px', border: `1.5px solid ${C.border}`, borderRadius: 8,
                                fontFamily: C.mono, fontSize: 13, fontWeight: 700, color: C.green,
                                textAlign: 'right', outline: 'none', background: C.white, boxSizing: 'border-box' }} />
                            {hasChange && (
                              <button style={btnStyle({ variant: 'green', size: 'xs', extra: { borderRadius: 7 } })}
                                onClick={() => inlinePriceSave(test.id, editPrices[test.id])}>✓</button>
                            )}
                          </div>
                        </td>
                        <td style={tdS}>
                          <button style={btnStyle({ variant: 'outline', size: 'sm' })} onClick={() => setPriceModal(test)}>
                            ✏️ Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ══════════════════ PAYMENT HISTORY ══════════════════ */}
        {activeTab === 'history' && (
          <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`,
            boxShadow: '0 1px 4px rgba(17,24,39,.05)', overflow: 'hidden' }}>
            <FilterBar>
              <SearchBar value={searchQ} onChange={e => setSearchQ(e.target.value)}
                placeholder="Search patient, ID, token…" width={300} />
              <select className="bm3-select-el" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                style={selectStyle({ minWidth: 170 })}>
                <option value="all">All Statuses</option>
                {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <RecordCount count={filteredOrders.length} label="records" />
            </FilterBar>
            <BillingTable rows={filteredOrders} onDiscount={setDiscountModal} showDiscountBtn />
          </div>
        )}

        {/* ══════════════════ DISCOUNTS ══════════════════ */}
        {activeTab === 'discounts' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 22 }}>
              <StatCard label="Total Discounts Given" value={fmt(totalDiscount)}
                color={C.orange} topColor={C.orange} icon="🏷️" sub={`${discountedOrders.length} orders`} />
              <StatCard label="Average Discount"
                value={discountedOrders.length ? fmt(totalDiscount / discountedOrders.length) : '₹0.00'}
                color={C.red} topColor={C.red} icon="📉" sub="Per discounted order" />
              <StatCard label="Discount % of Revenue"
                value={`${pct(totalDiscount, totalRevenue + totalDiscount)}%`}
                color={C.violet} topColor={C.violet} icon="📊" sub="Of gross revenue" />
            </div>
            <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`,
              boxShadow: '0 1px 4px rgba(17,24,39,.05)', overflow: 'hidden' }}>
              <div style={{ padding: '16px 22px', borderBottom: `1.5px solid ${C.border}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>
                  All Discounted Orders
                  <span style={{ fontSize: 13, fontWeight: 400, color: C.inkGhost, marginLeft: 8 }}>
                    ({discountedOrders.length})
                  </span>
                </span>
              </div>
              {discountedOrders.length === 0 ? (
                <div style={{ padding: 64, textAlign: 'center' }}>
                  <div style={{ width: 70, height: 70, borderRadius: '50%', background: C.bg,
                    border: `2px solid ${C.border}`, margin: '0 auto 18px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30 }}>🏷️</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: C.inkMid, marginBottom: 6 }}>No discounts applied yet</div>
                  <div style={{ fontSize: 13, color: C.inkSoft }}>Apply discounts from Payment History on BILLED orders.</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>{['Patient', 'Token', 'Original', 'Discount', 'Final Paid', 'Reason', 'Status', 'Date'].map(h => (
                        <th key={h} style={thS}>{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody>
                      {discountedOrders.map(o => (
                        <tr key={o.id} className="bm3-row">
                          <td style={tdS}>
                            <div style={{ fontWeight: 700 }}>{o.patient_name}</div>
                            <div style={{ fontSize: 11, color: C.inkGhost, fontFamily: C.mono, marginTop: 2 }}>{o.patient_id}</div>
                          </td>
                          <td style={tdS}><span style={{ fontFamily: C.mono, fontWeight: 700, color: C.primary }}>{o.token_number || '—'}</span></td>
                          <td style={tdS}><span style={{ fontFamily: C.mono, color: C.inkSoft, textDecoration: 'line-through' }}>{fmt(o.bill_amount)}</span></td>
                          <td style={tdS}><span style={{ fontFamily: C.mono, fontWeight: 700, color: C.orange }}>− {fmt(o.discount_amount)}</span></td>
                          <td style={tdS}><span style={{ fontFamily: C.mono, fontWeight: 800, color: C.green }}>{fmt(o.final_amount || o.bill_amount)}</span></td>
                          <td style={{ ...tdS, maxWidth: 220 }}>
                            <div style={{ fontSize: 12, color: C.inkSoft, fontStyle: 'italic',
                              background: C.bg, padding: '5px 10px', borderRadius: 8, lineHeight: 1.5 }}>
                              {o.discount_reason || '—'}
                            </div>
                          </td>
                          <td style={tdS}><StatusBadge status={o.status} /></td>
                          <td style={tdS}><span style={{ fontSize: 11, color: C.inkGhost }}>{fmtDate(o.order_date)}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* ══════════════════ MEMBER REPORTS ══════════════════ */}
        {activeTab === 'members' && (
          <>
            {/* Member leaderboard */}
            <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`,
              boxShadow: '0 1px 4px rgba(17,24,39,.05)', overflow: 'hidden', marginBottom: 20 }}>
              <div style={{ padding: '16px 22px', borderBottom: `1.5px solid ${C.border}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>👥 Member Revenue Leaderboard</span>
                {memberSummary.length > 0 && (
                  <span style={{ fontSize: 12, color: C.inkGhost, fontFamily: C.font }}>
                    Click a card to filter records below
                  </span>
                )}
              </div>

              {memberSummary.length === 0 ? (
                /* No billed_by data yet — backend hasn't populated the field */
                <div style={{ padding: '32px 22px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: C.bgAlt,
                    border: `1.5px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                    ℹ️
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.inkMid, marginBottom: 4 }}>
                      No member tracking data yet
                    </div>
                    <div style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.6 }}>
                      Billing records will appear here once the backend populates the{' '}
                      <code style={{ background: C.bgAlt, padding: '1px 6px', borderRadius: 4, fontFamily: C.mono }}>
                        billed_by
                      </code>{' '}
                      field on new bills. Existing orders without a billed_by assignment are shown in the "All Members" view below.
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '18px 22px', display: 'grid',
                  gridTemplateColumns: `repeat(${Math.min(memberSummary.length, 4)}, 1fr)`, gap: 12 }}>
                  {memberSummary.map((m, idx) => (
                    <div key={m.id}
                      onClick={() => setSelectedMember(prev => String(prev) === String(m.id) ? 'all' : String(m.id))}
                      style={{
                        background: String(selectedMember) === String(m.id) ? C.primaryBg : C.bg,
                        border: `1.5px solid ${String(selectedMember) === String(m.id) ? C.primary : C.border}`,
                        borderRadius: 12, padding: '14px 16px', cursor: 'pointer',
                        transition: 'all .15s',
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                          background: idx === 0 ? '#fef3c7' : idx === 1 ? '#f1f5f9' : idx === 2 ? '#fde8d8' : C.border,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14, fontWeight: 800, color: idx === 0 ? '#b45309' : C.inkSoft }}>
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: C.ink, flex: 1,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {m.name}
                        </div>
                      </div>
                      <div style={{ fontFamily: C.mono, fontWeight: 800, fontSize: 18, color: C.green, marginBottom: 4 }}>
                        {fmt(m.revenue)}
                      </div>
                      <div style={{ display: 'flex', gap: 10, fontSize: 11, color: C.inkSoft }}>
                        <span>{m.orders} orders</span>
                        {m.discounts > 0 && <span style={{ color: C.orange }}>−{fmt(m.discounts)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Detail panel */}
            <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`,
              boxShadow: '0 1px 4px rgba(17,24,39,.05)', overflow: 'hidden' }}>

              {/* Filters + actions */}
              <div style={{ padding: '14px 20px', borderBottom: `1.5px solid ${C.border}`,
                display: 'flex', gap: 12, alignItems: 'center', background: C.bg, flexWrap: 'wrap' }}>
                <select className="bm3-select-el" value={selectedMember}
                  onChange={e => setSelectedMember(e.target.value)}
                  style={selectStyle({ minWidth: 240 })}>
                  <option value="all">All Billing Members</option>
                  {/* First show members derived from leaderboard (have real billing data) */}
                  {memberSummary.length > 0 && (
                    <optgroup label="─── Active Members ───">
                      {memberSummary.map(m => (
                        <option key={m.id} value={String(m.id)}>
                          {m.name} ({m.orders} orders)
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {/* Also show members from API that may have 0 orders */}
                  {derivedMembers.filter(m =>
                    !memberSummary.find(s => String(s.id) === String(m.id))
                  ).length > 0 && (
                    <optgroup label="─── Other Members ───">
                      {derivedMembers
                        .filter(m => !memberSummary.find(s => String(s.id) === String(m.id)))
                        .map(m => (
                          <option key={m.id} value={String(m.id)}>{m.name}</option>
                        ))}
                    </optgroup>
                  )}
                </select>

                {/* Active filter chip */}
                {selectedMember !== 'all' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6,
                    background: C.primaryBg, border: `1.5px solid ${C.primaryBdr}`,
                    borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700,
                    color: C.primary }}>
                    👤 {selectedMemberName}
                    <button
                      onClick={() => setSelectedMember('all')}
                      style={{ background: 'none', border: 'none', cursor: 'pointer',
                        color: C.primary, fontSize: 14, padding: 0, lineHeight: 1,
                        fontWeight: 800, marginLeft: 2 }}>
                      ✕
                    </button>
                  </div>
                )}

                <SearchBar value={memberSearchQ} onChange={e => setMemberSearchQ(e.target.value)}
                  placeholder="Search patient, ID, token…" width={240} />
                <ReportActions
                  onPrint={handlePrintMemberReport}
                  onCSV={handleDownloadMemberCSV}
                  count={memberOrders.length}
                />
              </div>

              {/* Summary bar */}
              <div style={{ padding: '16px 22px', borderBottom: `1.5px solid ${C.border}`,
                display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', background: C.white }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: C.ink, marginRight: 6 }}>
                  {selectedMember === 'all' ? 'All Members' : selectedMemberName}
                </div>
                <SummaryPill label="Total Revenue" value={fmt(memberRevenue)} color={C.green} />
                <SummaryPill label="Total Orders" value={memberOrders.length} color={C.primary} />
                <SummaryPill label="Discounts Given" value={fmt(memberDiscount)} color={C.orange} />
                <SummaryPill label="Paid Orders"
                  value={memberOrders.filter(o => ['PAID','SAMPLE_COLLECTED','IN_PROGRESS','COMPLETED'].includes(o.status)).length}
                  color={C.green} />
              </div>

              <BillingTable rows={memberOrders} onDiscount={setDiscountModal} showDiscountBtn />
            </div>
          </>
        )}

        {/* ══════════════════ DAILY REPORTS ══════════════════ */}
        {activeTab === 'daily' && (
          <>
            {/* ── Date controls card ── */}
            <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`,
              boxShadow: '0 1px 4px rgba(17,24,39,.05)', overflow: 'hidden', marginBottom: 20 }}>
              <div style={{ padding: '18px 22px' }}>

                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: C.indigoBg,
                    border: `1.5px solid ${C.indigoBdr}`, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📅</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>Billing Report</div>
                    <div style={{ fontSize: 12, color: C.inkSoft, marginTop: 2 }}>
                      View and download billing records by single date or custom date range
                    </div>
                  </div>
                </div>

                {/* Mode toggle */}
                <div style={{ display: 'flex', gap: 4, background: C.bgAlt, borderRadius: 10,
                  padding: 4, marginBottom: 18, width: 'fit-content' }}>
                  {[
                    { key: 'single', label: '📅 Single Date' },
                    { key: 'range',  label: '📆 Date Range'  },
                  ].map(m => (
                    <button key={m.key} onClick={() => setDateMode(m.key)}
                      style={{
                        padding: '7px 18px', border: dateMode === m.key
                          ? `1.5px solid ${C.primaryBdr}` : '1.5px solid transparent',
                        borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                        fontFamily: C.font,
                        background: dateMode === m.key ? C.white : 'transparent',
                        color:      dateMode === m.key ? C.primary : C.inkSoft,
                        boxShadow:  dateMode === m.key ? '0 1px 4px rgba(37,99,235,.1)' : 'none',
                        outline: 'none',
                      }}>
                      {m.label}
                    </button>
                  ))}
                </div>

                {/* ── Single date mode ── */}
                {dateMode === 'single' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <label style={{ fontSize: 12, fontWeight: 700, color: C.inkSoft,
                      textTransform: 'uppercase', letterSpacing: '.06em' }}>Date</label>
                    <input type="date" value={reportDate} max={today()}
                      onChange={e => setReportDate(e.target.value)}
                      style={selectStyle({ minWidth: 180, height: 42 })} />
                    {reportDate !== today() && (
                      <button style={btnStyle({ variant: 'ghost', size: 'sm' })}
                        onClick={() => setReportDate(today())}>
                        Today
                      </button>
                    )}
                  </div>
                )}

                {/* ── Range mode ── */}
                {dateMode === 'range' && (
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                    {/* From */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: C.inkSoft,
                        textTransform: 'uppercase', letterSpacing: '.06em' }}>From</label>
                      <input type="date" value={fromDate} max={toDate || today()}
                        onChange={e => setFromDate(e.target.value)}
                        style={selectStyle({ minWidth: 180, height: 42 })} />
                    </div>

                    <div style={{ fontWeight: 800, color: C.inkGhost, fontSize: 18,
                      paddingBottom: 8, flexShrink: 0 }}>→</div>

                    {/* To */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: C.inkSoft,
                        textTransform: 'uppercase', letterSpacing: '.06em' }}>To</label>
                      <input type="date" value={toDate} min={fromDate} max={today()}
                        onChange={e => setToDate(e.target.value)}
                        style={selectStyle({ minWidth: 180, height: 42 })} />
                    </div>

                    {/* Preset shortcuts */}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingBottom: 2 }}>
                      {[
                        { label: 'Today',      fn: () => { const t = today(); setFromDate(t); setToDate(t); } },
                        { label: 'This Week',  fn: () => {
                          const t = new Date();
                          const day = t.getDay();
                          const mon = new Date(t);
                          mon.setDate(t.getDate() - (day === 0 ? 6 : day - 1));
                          setFromDate(mon.toLocaleDateString('en-CA'));
                          setToDate(today());
                        }},
                        { label: 'This Month', fn: () => {
                          const t = new Date();
                          setFromDate(`${t.toLocaleDateString('en-CA').slice(0,7)}-01`);
                          setToDate(today());
                        }},
                        { label: 'Last 30d',   fn: () => {
                          const t = new Date();
                          t.setDate(t.getDate() - 29);
                          setFromDate(t.toLocaleDateString('en-CA'));
                          setToDate(today());
                        }},
                      ].map(({ label, fn }) => (
                        <button key={label} onClick={fn}
                          style={btnStyle({ variant: 'outline', size: 'sm',
                            extra: { borderRadius: 8, fontSize: 11 } })}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Active range chip */}
                {dateMode === 'range' && fromDate && toDate && (
                  <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: C.inkSoft }}>Showing:</span>
                    <span style={{ padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                      background: C.indigoBg, color: C.indigo, border: `1px solid ${C.indigoBdr}` }}>
                      {dailyDateLabel}
                    </span>
                    <span style={{ fontSize: 11, color: C.inkSoft }}>
                      · {dailyOrders.length} record{dailyOrders.length !== 1 ? 's' : ''} found
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
              <StatCard label="Total Orders" value={dailyOrders.length}
                color={C.primary} topColor={C.primary} icon="📋"
                sub={dailyDateLabel} />
              <StatCard label="Billed Orders" value={dailyBilled}
                color={C.indigo} topColor={C.indigo} icon="✅"
                sub="Ordered + processed" />
              <StatCard label="Revenue Collected" value={fmt(dailyRevenue)}
                color={C.green} topColor={C.green} icon="💰"
                sub="From paid orders" />
              <StatCard label="Discounts Given" value={fmt(dailyDiscount)}
                color={C.orange} topColor={C.orange} icon="🏷️"
                sub={`${dailyOrders.filter(o => parseFloat(o.discount_amount||0)>0).length} orders`} />
            </div>

            {/* Payment method breakdown */}
            {dailyOrders.length > 0 && (() => {
              const dayPay = {};
              dailyOrders.forEach(o => {
                if (!['PAID','SAMPLE_COLLECTED','IN_PROGRESS','COMPLETED'].includes(o.status)) return;
                const m = o.payment_method || 'Cash';
                if (!dayPay[m]) dayPay[m] = { count: 0, amount: 0 };
                dayPay[m].count++;
                dayPay[m].amount += parseFloat(o.final_amount || o.bill_amount || 0);
              });
              if (!Object.keys(dayPay).length) return null;
              return (
                <div style={{ background: C.white, borderRadius: 14, padding: '18px 22px',
                  border: `1.5px solid ${C.border}`, marginBottom: 20,
                  boxShadow: '0 1px 4px rgba(17,24,39,.05)' }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.ink, marginBottom: 14 }}>
                    Payment Methods — {dailyDateLabel}
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {Object.entries(dayPay).sort((a,b)=>b[1].amount-a[1].amount).map(([m, d]) => {
                      const pc = PAY_COLORS[m] || PAY_COLORS.Other;
                      return (
                        <div key={m} style={{ background: pc.bg, border: `1.5px solid ${pc.color}22`,
                          borderRadius: 12, padding: '12px 18px', flex: 1, minWidth: 120 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: pc.color, marginBottom: 6 }}>{m}</div>
                          <div style={{ fontFamily: C.mono, fontWeight: 800, fontSize: 18, color: pc.color }}>
                            {fmt(d.amount)}
                          </div>
                          <div style={{ fontSize: 11, color: C.inkGhost, marginTop: 3 }}>{d.count} orders</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Table */}
            <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`,
              boxShadow: '0 1px 4px rgba(17,24,39,.05)', overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', borderBottom: `1.5px solid ${C.border}`,
                display: 'flex', gap: 12, alignItems: 'center', background: C.bg, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: C.ink }}>
                  Billing Records
                  <span style={{ fontWeight: 400, color: C.inkGhost, fontSize: 13, marginLeft: 8 }}>
                    {dailyDateLabel}
                  </span>
                </div>
                <SearchBar value={dailySearchQ} onChange={e => setDailySearchQ(e.target.value)}
                  placeholder="Search patient, ID, token…" width={250} />
                <ReportActions
                  onPrint={handlePrintDailyReport}
                  onCSV={handleDownloadDailyCSV}
                  count={dailyOrders.length}
                />
              </div>

              {dailyOrders.length === 0 ? (
                <div style={{ padding: 64, textAlign: 'center' }}>
                  <div style={{ width: 70, height: 70, borderRadius: '50%', background: C.bg,
                    border: `2px solid ${C.border}`, margin: '0 auto 18px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30 }}>📅</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: C.inkMid, marginBottom: 6 }}>
                    No records for {dailyDateLabel}
                  </div>
                  <div style={{ fontSize: 13, color: C.inkSoft }}>
                    {dateMode === 'single' && reportDate === today()
                      ? 'No billing activity has been recorded today yet.'
                      : 'No billing records were found for the selected date range.'}
                  </div>
                </div>
              ) : (
                <BillingTable rows={dailyOrders} onDiscount={setDiscountModal} showDiscountBtn />
              )}

              {/* Totals footer */}
              {dailyOrders.length > 0 && (
                <div style={{ padding: '16px 22px', borderTop: `2px solid ${C.border}`,
                  background: C.primaryBg, display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.primary }}>
                    {dateMode === 'range' ? 'Range Totals' : 'Day Totals'}
                  </span>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: C.inkSoft }}>Orders:</span>
                    <span style={{ fontFamily: C.mono, fontWeight: 700, color: C.ink }}>{dailyOrders.length}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: C.inkSoft }}>Gross:</span>
                    <span style={{ fontFamily: C.mono, fontWeight: 700, color: C.inkMid }}>
                      {fmt(dailyOrders.reduce((s,o)=>s+parseFloat(o.bill_amount||0),0))}
                    </span>
                  </div>
                  {dailyDiscount > 0 && (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: C.inkSoft }}>Discounts:</span>
                      <span style={{ fontFamily: C.mono, fontWeight: 700, color: C.orange }}>− {fmt(dailyDiscount)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 'auto' }}>
                    <span style={{ fontSize: 13, color: C.primary, fontWeight: 700 }}>Net Revenue:</span>
                    <span style={{ fontFamily: C.mono, fontWeight: 800, fontSize: 18, color: C.green }}>
                      {fmt(dailyRevenue)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ══════════════════ CALENDAR ══════════════════ */}
        {activeTab === 'calendar' && (
          <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`, padding: 20 }}>
            <Calendar />
          </div>
        )}
      </div>

      {priceModal    && <PriceEditModal test={priceModal}     onClose={() => setPriceModal(null)}    onSuccess={handlePriceUpdated}    />}
      {discountModal && <DiscountModal  order={discountModal} onClose={() => setDiscountModal(null)} onSuccess={handleDiscountApplied} />}
    </DashboardLayout>
  );
};

export default BillingManager;